import React, { useState, useEffect, useContext } from "react";
import { Heart, Share2, Camera, ArrowUp, Calendar, Eye } from "lucide-react";
import { fetchAllGalleries, getImageSrc } from "../apiService";
import { motion } from "framer-motion";
import { useLanguage } from "../utils/LanguageContext";
import { useNavigate } from "react-router-dom";
import Footer from "../components/footer";

const PhotoAlbum = () => {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hovered, setHovered] = useState(null);
  const [likedGalleries, setLikedGalleries] = useState(new Set());
  const { language } = useLanguage();
  const navigate = useNavigate();

  useEffect(() => {
    const loadGalleries = async () => {
      setLoading(true);
      try {
        const response = await fetchAllGalleries();
        setGalleries(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error("Error loading galleries:", error);
        setGalleries([]);
      } finally {
        setLoading(false);
      }
    };

    loadGalleries();
  }, []);

  const handleLike = (galleryId) => {
    setLikedGalleries((prev) => {
      const newSet = new Set(prev);
      newSet.has(galleryId) ? newSet.delete(galleryId) : newSet.add(galleryId);
      return newSet;
    });
  };

  const handleShare = async (gallery) => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: gallery.title,
          text: gallery.description || (language === 'MR' ? "हे फोटो अल्बम पहा" : "Check out this photo album"),
          url: window.location.href,
        });
      } else {
        await navigator.clipboard.writeText(window.location.href);
        alert(language === 'MR' ? "लिंक कॉपी केला गेला!" : "Link copied!");
      }
    } catch (error) {
      console.log("Sharing failed:", error);
    }
  };

  // Navigate to individual gallery view
  const handleGalleryClick = (gallery) => {
    navigate(`/gallery/${gallery._id}`, { state: { gallery } });
  };

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'MR' ? 'mr-IN' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center pt-24">
        <div className="text-center">
          <div className="w-20 h-20 border-4 border-gray-200 border-t-red-600 rounded-full animate-spin mx-auto mb-6"></div>
          <p className="text-red-700 font-semibold text-lg font-marathi">
            {language === 'MR' ? 'फोटो अल्बम लोड होत आहे...' : 'Loading photo album...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-20">
        {/* Header Section */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-6xl font-medium mb-6 text-red-800 font-marathi">
            {language === 'MR' ? 'मित्रांबरोबर जगाच्या पाठीवर' : 'World of Memories'}
          </h1>
          <p className="text-gray-700/90 max-w-3xl mx-auto text-xl font-medium leading-relaxed font-marathi mb-8">
            {language === 'MR' ? 'आमच्या कॅमेर्यात कॅप्चर केलेले सुंदर क्षण अन्वेषण करा' : 'Explore beautiful moments captured by our camera'}
          </p>
        </motion.div>

        {/* Gallery Grid */}
        {galleries.length === 0 ? (
          <div className="text-center py-20">
            <Camera className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-marathi">
              {language === 'MR' ? 'अद्याप कोणतेही फोटो अल्बम उपलब्ध नाही' : 'No photo albums available yet'}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {galleries.map((gallery, index) => (
              <motion.div
                key={gallery._id || index}
                className="group bg-white rounded-2xl overflow-hidden shadow-md hover:shadow-xl transition-all duration-300 border border-gray-200 cursor-pointer"
                onMouseEnter={() => setHovered(index)}
                onMouseLeave={() => setHovered(null)}
                onClick={() => handleGalleryClick(gallery)}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                whileHover={{ y: -5 }}
              >
                {/* Image Container */}
                <div className="relative h-48 sm:h-56 overflow-hidden bg-gray-100">
                  {gallery.photos?.[0]?.imageUrl ? (
                    <motion.img
                      src={getImageSrc(gallery.photos[0].imageUrl)}
                      alt={gallery.title || "Photo Album"}
                      className="w-full h-full object-cover"
                      whileHover={{ scale: 1.05 }}
                      transition={{ duration: 0.3 }}
                    />
                  ) : (
                    <div className="w-full h-full bg-gray-100 flex items-center justify-center">
                      <Camera className="w-12 h-12 text-gray-400" />
                    </div>
                  )}

                  {/* Photo Count Badge */}
                  <div className="absolute top-3 left-3 bg-black/70 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1">
                    <Camera className="w-3 h-3" />
                    {gallery.photos?.length || 0}
                  </div>

                  {/* View Gallery Badge */}
                  <div className="absolute top-3 right-3 bg-red-600/90 text-white px-2 py-1 rounded-full text-xs font-medium flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <Eye className="w-3 h-3" />
                    {language === 'MR' ? 'पहा' : 'View'}
                  </div>

                  {/* Hover Overlay */}
                  <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center">
                    <div className="flex gap-2">
                      <motion.button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleLike(gallery._id);
                        }}
                        className={`p-2 rounded-full backdrop-blur-sm transition-all duration-200 ${
                          likedGalleries.has(gallery._id)
                            ? "bg-red-500 text-white"
                            : "bg-white/20 text-white hover:bg-white/30"
                        }`}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Heart
                          className="w-4 h-4"
                          fill={likedGalleries.has(gallery._id) ? "currentColor" : "none"}
                        />
                      </motion.button>
                      <motion.button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShare(gallery);
                        }}
                        className="p-2 rounded-full bg-white/20 text-white hover:bg-white/30 backdrop-blur-sm transition-all duration-200"
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                      >
                        <Share2 className="w-4 h-4" />
                      </motion.button>
                    </div>
                  </div>
                </div>

                {/* Content */}
                <div className="p-4">
                  <h3 className="font-bold text-gray-900 text-lg mb-2 font-marathi line-clamp-1">
                    {gallery.title || (language === 'MR' ? 'नाव नसलेले अल्बम' : 'Untitled Album')}
                  </h3>
                  
                  {gallery.description && (
                    <p className="text-gray-600 text-sm mb-3 font-marathi line-clamp-2">
                      {gallery.description}
                    </p>
                  )}

                  {/* Meta Information */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    {gallery.createdAt && (
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {formatDate(gallery.createdAt)}
                      </span>
                    )}
                    {gallery.author && (
                      <span className="font-medium">
                        {gallery.author}
                      </span>
                    )}
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Scroll to Top Button */}
      <motion.button
        onClick={scrollToTop}
        className="fixed bottom-8 right-8 p-3 text-red-600 hover:text-red-800 bg-white/90 backdrop-blur-sm rounded-full shadow-lg border border-red-200 z-50 flex items-center justify-center"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.95 }}
        title={language === 'MR' ? 'वर जा' : 'Go up'}
        aria-label="Scroll to top"
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1 }}
      >
        <ArrowUp className="w-5 h-5" />
      </motion.button>

      <style jsx global>{`
        @font-face {
          font-family: "Marathi";
          src: url("/fonts/NotoSansDevanagari-Regular.ttf") format("truetype");
          font-display: swap;
        }
        .font-marathi {
          font-family: "Marathi", "Noto Sans Devanagari", sans-serif;
        }
        .line-clamp-1 {
          display: -webkit-box;
          -webkit-line-clamp: 1;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
        .line-clamp-2 {
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }
      `}</style>
      <Footer />
    </div>
  );
};

export default PhotoAlbum;